import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mitarbeiter } from '../types';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const MitarbeiterList: React.FC = () => {
  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', qualifikation: '', typ: 'INTERN', stundensatz_extern: '' });
  const [saving, setSaving] = useState(false);
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    setFehler(null);
    axios.get<Mitarbeiter[]>(`${API_URL}/api/mitarbeiter`)
      .then(r => setMitarbeiter(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Mitarbeiter konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/mitarbeiter`, {
        ...form,
        stundensatz_extern: form.stundensatz_extern ? parseFloat(form.stundensatz_extern) : null,
      });
      setShowForm(false);
      setForm({ name: '', email: '', qualifikation: '', typ: 'INTERN', stundensatz_extern: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAktiv = async (m: Mitarbeiter) => {
    await axios.patch(`${API_URL}/api/mitarbeiter/${m.mitarbeiter_id}`, { aktiv: !m.aktiv });
    load();
  };

  if (loading) return <div style={{ padding: 16 }}>Lade Mitarbeiter…</div>;

  if (fehler) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'var(--bone-2)', border: '1px solid var(--bone-3)', borderRadius: 0,
          fontSize: 13, color: 'var(--red)',
        }}>
          <span>{fehler}</span>
          <button
            className="k-btn k-btn--danger k-btn--sm"
            onClick={load}
            style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Mitarbeiter</h2>
        <button
          className={`k-btn ${showForm ? 'k-btn--ghost' : 'k-btn--primary'}`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Abbrechen' : '+ Neu'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <input required placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ padding: 8 }} />
            <input placeholder="E-Mail" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ padding: 8 }} />
            <input placeholder="Qualifikation" value={form.qualifikation} onChange={e => setForm(f => ({ ...f, qualifikation: e.target.value }))} style={{ padding: 8 }} />
            <select value={form.typ} onChange={e => setForm(f => ({ ...f, typ: e.target.value }))} style={{ padding: 8 }}>
              <option value="INTERN">Intern</option>
              <option value="EXTERN">Extern</option>
              <option value="SUBUNTERNEHMER">Subunternehmer</option>
            </select>
            <input placeholder="Stundensatz (extern) €" type="number" value={form.stundensatz_extern} onChange={e => setForm(f => ({ ...f, stundensatz_extern: e.target.value }))} style={{ padding: 8 }} />
          </div>
          <button type="submit" className="k-btn k-btn--primary" disabled={saving} style={{ marginTop: 12 }}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </form>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bone-3)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>E-Mail</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Qualifikation</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Typ</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>€/h</th>
            <th style={{ padding: '8px 12px', textAlign: 'center' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {mitarbeiter.map(m => (
            <React.Fragment key={m.mitarbeiter_id}>
              <tr style={{ borderBottom: '1px solid var(--bone-3)', opacity: m.aktiv ? 1 : 0.5 }}>
                <td style={{ padding: '8px 12px', fontWeight: 500 }}>{m.name}</td>
                <td style={{ padding: '8px 12px' }}>{m.email || '—'}</td>
                <td style={{ padding: '8px 12px' }}>{m.qualifikation || '—'}</td>
                <td style={{ padding: '8px 12px' }}>{m.typ}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  {m.stundensatz_extern != null ? `${m.stundensatz_extern.toFixed(2)} €` : '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                  <button
                    className={`k-btn k-btn--sm ${m.aktiv ? 'k-btn--primary' : 'k-btn--ghost'}`}
                    onClick={() => handleToggleAktiv(m)}
                  >
                    {m.aktiv ? 'Aktiv' : 'Inaktiv'}
                  </button>{' '}
                  <button
                    className="k-btn k-btn--ghost k-btn--sm"
                    onClick={() => setAkteId(akteId === m.mitarbeiter_id ? null : m.mitarbeiter_id)}
                  >
                    {akteId === m.mitarbeiter_id ? 'Akte schließen' : 'Akte'}
                  </button>
                </td>
              </tr>
              {akteId === m.mitarbeiter_id && (
                <tr style={{ opacity: m.aktiv ? 1 : 0.5 }}>
                  <td colSpan={6} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                    <AktivitaetenBlock entitaetTyp="mitarbeiter" entitaetId={m.mitarbeiter_id} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {mitarbeiter.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>Keine Mitarbeiter vorhanden.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MitarbeiterList;
